import {
  convertCanvasToImage,
  download,
  JupiterTheme,
} from '@jupiterone/jupiter-web-toolkit';
import FilterIcon from '@jupiterone/react-brand-icons/jupiterone/filter';
import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect } from 'react';
import CameraIcon from 'react-feather/dist/icons/camera';
import EyeIcon from 'react-feather/dist/icons/eye';
import LockIcon from 'react-feather/dist/icons/lock';
import MaxIcon from 'react-feather/dist/icons/maximize';
import MinIcon from 'react-feather/dist/icons/minimize';
import UndoIcon from 'react-feather/dist/icons/rotate-ccw';
import RedoIcon from 'react-feather/dist/icons/rotate-cw';
import CenterIcon from 'react-feather/dist/icons/target';
import UnlockIcon from 'react-feather/dist/icons/unlock';
import DataIcon from 'react-feather/dist/icons/database';
import Check from 'react-feather/dist/icons/check';
import { useBooleanState } from 'react-use-object-state';
import { useGraphNetwork } from '../../hooks/useGraphNetwork';
import { GraphFilters, GraphViewMode, GraphViewType } from '../types';
import GraphTypeButton from './GraphTypeButton';
import clsx from 'clsx';

interface Props {
  viewMode: GraphViewMode;
  filters: GraphFilters;
  onChangeFilters: (newFilters: GraphFilters) => void;
  onToggleFiltersVisible: () => void;
  setViewMode: (mode: GraphViewMode) => void;
  onUpdateGraphType: (type: GraphViewType) => void;
  graphType: GraphViewType;
  containerRef: React.MutableRefObject<Element | null>;
  undo?: VoidFunction;
  redo?: VoidFunction;
  onToggleMoveAllConnectedNodes: (state: boolean) => void;
  canLoadMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  totalResultCount?: number;
  loadedResultCount: number;
}

export const DATA_TEST_ID_LOAD_MORE_BUTTON = 'loadMore-button';
export const DATA_TEST_ID_ALL_LOADED_BUTTON = 'allLoaded-button';
export const DATA_TEST_ID_LOADING_MORE_PROGRESS = 'loadingMore-progress';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    root: {
      position: 'absolute',
      bottom: theme.spacing(1.25),
      left: theme.spacing(1.25),
    },
    busyRoot: {
      filter: 'blur(1.5px)',
      opacity: 0.8,
      pointerEvents: 'none',
    },
    toolGroup: {
      marginRight: theme.spacing(1),
      background: theme.palette.grey[50],
      boxShadow: theme.shadows[4],
      position: 'relative',
      '& button:not(:first-child)': {
        '&:before': {
          background: theme.palette.grey[300],
          content: `''`,
          display: 'block',
          left: -1,
          top: -1,
          position: 'absolute',
          height: 'calc(100% + 2px)',
          width: theme.spacing(0.125),
        },
      },
    },
    button: {
      color: theme.palette.grey[600],
      padding: 0,
      minHeight: theme.spacing(4.5),
      minWidth: theme.spacing(4.5),
      borderRadius: 0,
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.primary.dark,
      },
      '&.active': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.blue.main,
      },
    },
    icon: {
      strokeWidth: 1.2,
    },
    floatingButton: {
      marginBottom: theme.spacing(1),
      background: theme.palette.grey[50],
      boxShadow: theme.shadows[4],
    },
    loadMoreButton: {
      color: theme.palette.secondary.main,
      '&:active, &:focus': {
        color: theme.palette.secondary.main,
        borderColor: 'transparent',
        background: theme.palette.grey[50],
      },
    },
    allLoadedButton: {
      color: theme.palette.primary.main,
      '&:active, &:focus': {
        color: theme.palette.primary.main,
        borderColor: 'transparent',
        background: theme.palette.grey[50],
      },
      '&:hover': {
        cursor: 'default',
        color: theme.palette.primary.main,
        background: theme.palette.grey[50],
      },
    },
  })
);

const GraphViewOptions = ({
  setViewMode,
  onToggleFiltersVisible,
  viewMode,
  graphType,
  filters,
  onChangeFilters,
  onUpdateGraphType,
  containerRef,
  undo,
  redo,
  onToggleMoveAllConnectedNodes,
  canLoadMore,
  loadingMore,
  totalResultCount,
  loadedResultCount,
  onLoadMore,
}: Props) => {
  const classes = useStyles();
  const [network] = useGraphNetwork();
  const internalMoveAllconnectedNodes = useBooleanState(false);
  useEffect(() => {
    onToggleMoveAllConnectedNodes(internalMoveAllconnectedNodes.state);
  }, [internalMoveAllconnectedNodes.state]);

  const showAllHiddenNodes = () => {
    onChangeFilters({ ...filters, hiddenNodes: [] });
  };

  const downloadCanvas = () => {
    if (!containerRef.current) {
      return;
    }
    const canvas = containerRef.current.querySelector(
      'canvas'
    ) as HTMLCanvasElement;
    const data = convertCanvasToImage(canvas);
    download(data, 'j1-graph-view.png');
  };

  return (
    <div className={classes.root}>
      {canLoadMore ? (
        <Tooltip
          title={
            totalResultCount
              ? `You have loaded ${loadedResultCount} of ${totalResultCount} total results.`
              : 'This query has more pages of data.  Consider narrowing down your query to focus your results.'
          }
          placement="top"
          enterDelay={500}
          data-testid={DATA_TEST_ID_LOAD_MORE_BUTTON}
        >
          <Button
            aria-label="Load More"
            className={clsx(classes.floatingButton, classes.loadMoreButton)}
            variant="contained"
            size="small"
            startIcon={
              loadingMore ? (
                <CircularProgress
                  data-testid={DATA_TEST_ID_LOADING_MORE_PROGRESS}
                  size="1.5rem"
                />
              ) : (
                <DataIcon />
              )
            }
            onClick={onLoadMore}
          >
            Load More Results
          </Button>
        </Tooltip>
      ) : (
        <Tooltip
          title="All results has been loaded for this query."
          placement="top"
          enterDelay={500}
          data-testid={DATA_TEST_ID_ALL_LOADED_BUTTON}
        >
          <Button
            aria-label="All results loaded"
            className={clsx(classes.floatingButton, classes.allLoadedButton)}
            variant="contained"
            size="small"
            startIcon={<Check />}
          >
            All Data Loaded
          </Button>
        </Tooltip>
      )}
      <div className={classes.toolGroup}>
        <Tooltip title="Toggle filters" placement="top" enterDelay={500}>
          <Button
            aria-label="Toggle filters"
            className={classes.button}
            variant="text"
            size="small"
            onClick={onToggleFiltersVisible}
          >
            <FilterIcon className={classes.icon} />
          </Button>
        </Tooltip>
        <Tooltip
          title={` Movement for connected nodes is ${
            internalMoveAllconnectedNodes.state ? 'unlocked' : 'locked'
          }`}
          placement="top"
          enterDelay={500}
        >
          <Button
            aria-label={` Movement for connected nodes is ${
              internalMoveAllconnectedNodes.state ? 'unlocked' : 'locked'
            }`}
            className={classes.button}
            variant="text"
            size="small"
            onClick={internalMoveAllconnectedNodes.toggle}
          >
            {internalMoveAllconnectedNodes.state ? (
              <UnlockIcon className={classes.icon} />
            ) : (
              <LockIcon className={classes.icon} />
            )}
          </Button>
        </Tooltip>
        <GraphTypeButton
          graphType={graphType}
          onUpdateGraphType={onUpdateGraphType}
          inheritedClasses={{ icon: classes.icon, button: classes.button }}
        />
        <>
          <Tooltip
            title={viewMode === 'max' ? 'Exit full screen' : 'Full screen'}
            placement="top"
            enterDelay={500}
          >
            <Button
              aria-label={
                viewMode === 'max' ? 'Exit full screen' : 'Full screen'
              }
              className={classes.button}
              variant="text"
              size="small"
              onClick={() => setViewMode(viewMode === 'max' ? 'embed' : 'max')}
            >
              {viewMode === 'embed' && <MaxIcon className={classes.icon} />}
              {viewMode === 'max' && <MinIcon className={classes.icon} />}
            </Button>
          </Tooltip>
        </>
        <Tooltip title="Download image" placement="top" enterDelay={500}>
          <Button
            aria-label="Download image"
            className={classes.button}
            variant="text"
            size="small"
            onClick={downloadCanvas}
          >
            <CameraIcon className={classes.icon} />
          </Button>
        </Tooltip>
        <Tooltip title="Center Graph" placement="top" enterDelay={500}>
          <Button
            aria-label="Center Graph"
            className={classes.button}
            variant="text"
            size="small"
            onClick={() => network?.fit()}
          >
            <CenterIcon className={classes.icon} />
          </Button>
        </Tooltip>
        {undo && (
          <Tooltip title="Undo" placement="top" enterDelay={500}>
            <Button
              aria-label="Undo"
              className={classes.button}
              variant="text"
              size="small"
              onClick={undo}
            >
              <UndoIcon className={classes.icon} />
            </Button>
          </Tooltip>
        )}
        {redo && (
          <Tooltip title="Redo" placement="top" enterDelay={500}>
            <Button
              aria-label="Redo"
              className={classes.button}
              variant="text"
              size="small"
              onClick={redo}
            >
              <RedoIcon className={classes.icon} />
            </Button>
          </Tooltip>
        )}
        {filters.hiddenNodes.length > 0 && (
          <>
            <Tooltip title="Show hidden nodes" placement="top" enterDelay={500}>
              <Button
                data-testid="show-hidden-nodes"
                aria-label="Show hidden nodes"
                className={classes.button}
                variant="text"
                size="small"
                onClick={showAllHiddenNodes}
              >
                <EyeIcon className={classes.icon} />
              </Button>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};

export default GraphViewOptions;
